import { useEffect, useRef, useState } from "react";

/**
 * Infinite scroll: calls `onLoadMore` when the returned sentinel ref scrolls
 * near the bottom of `root`. `onLoadMore` resolves to `true` once a page has
 * landed; the sentinel is then re-checked, so one still in view asks for the
 * next page right away. After a failed load (rejected or `false`) the next
 * page is only requested once the sentinel scrolls back into view.
 * The signal aborts when there is nothing more to load or on unmount
 */
export function useLoadMore({
  root,
  hasMore,
  isLoading,
  onLoadMore,
}: {
  root: HTMLElement | null;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: (signal: AbortSignal) => Promise<boolean>;
}): (sentinel: HTMLElement | null) => void {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!hasMore) return undefined;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    return () => abortController.abort("Load more stopped");
  }, [hasMore]);

  useEffect(() => {
    if (!root || !sentinel || !hasMore) return undefined;

    let isObserving = true;
    const observer = new IntersectionObserver(
      async ([entry]) => {
        const signal = abortControllerRef.current?.signal;
        if (!entry?.isIntersecting || !signal || isLoadingRef.current) return;

        const didLoad = await onLoadMoreRef.current(signal).catch(() => false);
        if (!didLoad || !isObserving) return;

        observer.unobserve(sentinel);
        observer.observe(sentinel);
      },
      { root, rootMargin: "200px" },
    );
    observer.observe(sentinel);

    return () => {
      isObserving = false;
      observer.disconnect();
    };
  }, [root, sentinel, hasMore]);

  return setSentinel;
}
